/**
 * Release notes as a short read: what is new since the reader last looked, opened by
 * itself once after a release, and every release when they ask for it. Each change is
 * a bold theme beside a light sentence; the newest release's first theme leads.
 * Releases beyond the newest two fold behind one disclosure so a long absence stays
 * one screen.
 */
import type { ReleaseNoteItemResponse, ReleaseResponse } from '@contract-analyzer/api-client';
import { ChevronDownIcon } from 'lucide-react';
import { useId, useRef } from 'react';

import {
  describeReleases,
  foldReleases,
  formatReleaseDate,
  releasesInView,
  type ReleaseNewsView,
} from './release-news-model';
import { MarkdownContent } from '@/components/markdown-content';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { UseReleaseNewsReturn } from '@/hooks/use-release-news';
import { cn } from '@/lib/utils';

const BODY_CLASS = 'font-light leading-relaxed text-muted-foreground';

function Change({ item, lead }: { readonly item: ReleaseNoteItemResponse; readonly lead: boolean }) {
  return (
    // On wider screens the wrapper dissolves so the theme and its sentence share a grid row.
    <div className="flex flex-col gap-1 sm:contents">
      <dt
        className={cn(
          'font-bold tracking-tight text-foreground',
          lead ? 'text-lg sm:col-span-2' : 'text-sm',
        )}
      >
        {item.title}
      </dt>
      <dd className={cn('min-w-0', lead && 'sm:col-span-2 sm:pb-3')}>
        <MarkdownContent className={cn(BODY_CLASS, lead && 'text-foreground')} content={item.body} />
      </dd>
    </div>
  );
}

function ReleaseSection({
  release,
  labelled,
  leads,
}: {
  readonly release: ReleaseResponse;
  /** Name the release above its changes; a lone release is already named in the header. */
  readonly labelled: boolean;
  /** Its first change leads the dialog. */
  readonly leads: boolean;
}) {
  const headingId = useId();
  return (
    <section aria-labelledby={labelled ? headingId : undefined} className="flex flex-col gap-4">
      {labelled ? (
        <header className="flex items-center gap-3">
          <h3 className="shrink-0 text-sm font-bold tracking-tight tabular-nums" id={headingId}>
            {release.version}
          </h3>
          <time
            className="shrink-0 text-eyebrow text-muted-foreground tabular-nums"
            dateTime={release.released_on}
          >
            {formatReleaseDate(release.released_on)}
          </time>
          <span aria-hidden="true" className="h-px min-w-4 flex-1 bg-border" />
        </header>
      ) : null}
      <dl className="flex flex-col gap-4 sm:grid sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)] sm:gap-x-6 sm:gap-y-3">
        {release.items.map((item, index) => (
          <Change item={item} key={item.title} lead={leads && index === 0} />
        ))}
      </dl>
    </section>
  );
}

function EarlierReleases({ releases }: { readonly releases: readonly ReleaseResponse[] }) {
  return (
    <Collapsible className="flex flex-col gap-6">
      <CollapsibleTrigger asChild>
        <Button className="self-start [&[data-state=open]>svg]:rotate-180" size="sm" variant="ghost">
          {releases.length === 1 ? '1 earlier release' : `${String(releases.length)} earlier releases`}
          <ChevronDownIcon data-icon="inline-end" />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-8">
        {releases.map((release) => (
          <ReleaseSection key={release.version} labelled leads={false} release={release} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

function ReleaseNotes({
  view,
  releases,
}: {
  readonly view: ReleaseNewsView;
  readonly releases: readonly ReleaseResponse[];
}) {
  const { expanded, earlier } = foldReleases(releases);
  const labelled = view === 'all' || releases.length > 1;
  return (
    <>
      {expanded.map((release, index) => (
        <ReleaseSection
          key={release.version}
          labelled={labelled}
          leads={view === 'unseen' && index === 0}
          release={release}
        />
      ))}
      {earlier.length > 0 ? <EarlierReleases releases={earlier} /> : null}
    </>
  );
}

export function ReleaseNewsDialog({ releaseNews }: { readonly releaseNews: UseReleaseNewsReturn }) {
  const { view, data, close, showAll } = releaseNews;
  const primaryRef = useRef<HTMLButtonElement>(null);
  if (view === null || data === null) return null;

  const releases = releasesInView(data, view);
  const moreToRead = view === 'unseen' && data.releases.length > releases.length;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent
        className="flex max-h-[min(85vh,46rem)] flex-col gap-6 sm:max-w-2xl"
        onOpenAutoFocus={(event) => {
          // Land on the one action, not on the first link inside the notes.
          event.preventDefault();
          primaryRef.current?.focus();
        }}
      >
        <DialogHeader className="text-left">
          <DialogTitle className="text-xl font-bold tracking-tight">
            {view === 'unseen' ? 'What’s new' : 'Release notes'}
          </DialogTitle>
          <DialogDescription className="font-light tabular-nums">
            {describeReleases(view, releases)}
          </DialogDescription>
        </DialogHeader>

        <div
          // Remounting on a view change starts the history at its top.
          className="-mx-1 flex min-h-0 flex-col gap-8 overflow-y-auto px-1"
          key={view}
        >
          <ReleaseNotes releases={releases} view={view} />
        </div>

        <DialogFooter>
          {moreToRead ? (
            <Button onClick={showAll} variant="ghost">
              All release notes
            </Button>
          ) : null}
          <Button onClick={close} ref={primaryRef}>
            {view === 'unseen' ? 'Got it' : 'Done'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
